import type { TierGaugeTier } from "@/components/charts/TierGauge"
import type { SubvalueByTimestamp } from "@/types/RunMetricOutput"

// Provisional bands (semitone std). Low = monotone is the concern.
// Calibrate against real reviewer data before treating as fixed.
const MONOTONE_ST = 1
export const FLAT_ST = 2
export const EXPRESSIVE_ST = 4

// Monotone-region detection over the Hz contour. Perception of "monotone" is a
// multi-second property, so we measure the local semitone std (same unit as the
// metric) over a several-second window — not point-to-point jumps.
const FLAT_STD_ST = 1.5 // local pitch std below this over the window reads as flat
const EXPRESSIVE_STD_ST = 3 // local pitch std above this reads as expressive
const NEIGHBORHOOD_SECONDS = 5 // window for the local-variation check
const MIN_FLAT_SECONDS = 4 // ignore flat stretches shorter than this
const MIN_EXPRESSIVE_SECONDS = 3 // ignore expressive bursts shorter than this
export const GAP_FACTOR = 1.75 // a time gap > GAP_FACTOR×binWidth is unvoiced → break the run

// Region fill colors (rgb triples) shared by the contour highlights and the
// audio-player regions, so both surfaces stay visually in sync.
export const MONOTONE_FILL = "245, 158, 11" // amber
export const EXPRESSIVE_FILL = "34, 197, 94" // green

export function pitchTier(semitoneStd: number): TierGaugeTier {
  if (semitoneStd < MONOTONE_ST) {
    return {
      label: "Monotone",
      variant: "danger",
      blurb: "Pitch barely moves — flat, robotic delivery.",
    }
  }
  if (semitoneStd < FLAT_ST) {
    return {
      label: "Flat",
      variant: "warning",
      blurb: "Limited pitch movement — sounds somewhat flat.",
    }
  }
  if (semitoneStd < EXPRESSIVE_ST) {
    return {
      label: "Neutral",
      variant: "success",
      blurb: "Neutral, conversational intonation.",
    }
  }
  return {
    label: "Expressive",
    variant: "info",
    blurb: "Highly expressive, dynamic intonation.",
  }
}

interface ContourPoint {
  start: number
  end: number
  hz: number
}

export function contourPoints(contour: SubvalueByTimestamp[]): ContourPoint[] {
  return contour
    .filter((p) => (p.float_value ?? 0) > 0 && p.end_offset > p.start_offset)
    .map((p) => ({
      start: p.start_offset,
      end: p.end_offset,
      hz: p.float_value as number,
    }))
    .sort((a, b) => a.start - b.start)
}

export function medianBinWidth(points: ContourPoint[]): number {
  if (!points.length) {
    return 1
  }
  const widths = points.map((p) => p.end - p.start).sort((a, b) => a - b)
  return widths[Math.floor(widths.length / 2)] || 1
}

export function standardDeviation(values: number[]): number {
  const mean = values.reduce((a, b) => a + b, 0) / values.length
  const variance =
    values.reduce((a, b) => a + (b - mean) ** 2, 0) / values.length
  return Math.sqrt(variance)
}

type SpanKind = "monotone" | "expressive"

interface VariationSpan {
  start: number
  end: number
  alpha: number
  kind: SpanKind
}

// Classify each window by local semitone std over a multi-second window, then merge
// same-kind runs into spans: monotone where std stays below FLAT_STD_ST, expressive
// where it stays above EXPRESSIVE_STD_ST. Runs break on unvoiced gaps and are never
// bridged across them (piecewise). Fill opacity scales with how strong each span is.
export function detectSpans(contour: SubvalueByTimestamp[]): VariationSpan[] {
  const points = contourPoints(contour)
  if (points.length < 4) {
    return []
  }
  const binWidth = medianBinWidth(points)
  const gapTolerance = binWidth * GAP_FACTOR
  const windowCount = Math.max(3, Math.round(NEIGHBORHOOD_SECONDS / binWidth))

  // Split into contiguous (voiced) runs.
  const runs: ContourPoint[][] = []
  let run: ContourPoint[] = [points[0]]
  for (let i = 1; i < points.length; i++) {
    if (points[i].start - points[i - 1].end > gapTolerance) {
      runs.push(run)
      run = [points[i]]
    } else {
      run.push(points[i])
    }
  }
  runs.push(run)

  const classify = (std: number): SpanKind | null => {
    if (std < FLAT_STD_ST) {
      return "monotone"
    }
    if (std > EXPRESSIVE_STD_ST) {
      return "expressive"
    }
    return null
  }
  const minSeconds = (kind: SpanKind): number =>
    kind === "monotone" ? MIN_FLAT_SECONDS : MIN_EXPRESSIVE_SECONDS
  const spanAlpha = (kind: SpanKind, meanStd: number): number => {
    const strength =
      kind === "monotone"
        ? 1 - meanStd / FLAT_STD_ST // flatter → stronger
        : Math.min(1, (meanStd - EXPRESSIVE_STD_ST) / EXPRESSIVE_STD_ST) // livelier → stronger
    return Math.min(0.6, Math.max(0.3, 0.3 + 0.3 * strength))
  }

  const spans: VariationSpan[] = []
  for (const segment of runs) {
    if (segment.length < 2) {
      continue
    }
    // semitone scale; std is invariant to the reference, so log2(hz) is enough.
    const semis = segment.map((p) => 12 * Math.log2(p.hz))
    const localStd = segment.map((_, i) => {
      const lo = Math.max(0, i - Math.floor(windowCount / 2))
      const hi = Math.min(segment.length, lo + windowCount)
      return standardDeviation(semis.slice(lo, hi))
    })
    const kinds = localStd.map(classify)

    let i = 0
    while (i < segment.length) {
      const kind = kinds[i]
      if (kind === null) {
        i++
        continue
      }
      let j = i
      while (j < segment.length && kinds[j] === kind) {
        j++
      }
      const start = segment[i].start
      const end = segment[j - 1].end
      if (end - start >= minSeconds(kind)) {
        const meanStd =
          localStd.slice(i, j).reduce((a, b) => a + b, 0) / (j - i)
        spans.push({ start, end, alpha: spanAlpha(kind, meanStd), kind })
      }
      i = j
    }
  }
  return spans
}

// Contour as a chart series in semitones relative to the clip's mean (0 = average
// pitch), so it's register-independent — masculine/feminine voices plot the same.
// Null breaks across unvoiced gaps keep the line piecewise (connectNulls={false}).
export function contourSeries(
  points: ContourPoint[],
  gapTolerance: number,
): Array<{ t: number; st: number | null; hz: number | null }> {
  if (!points.length) {
    return []
  }
  const refSemis =
    points.reduce((a, p) => a + 12 * Math.log2(p.hz), 0) / points.length
  const series: Array<{ t: number; st: number | null; hz: number | null }> = []
  points.forEach((p, i) => {
    series.push({ t: p.start, st: 12 * Math.log2(p.hz) - refSemis, hz: p.hz })
    const next = points[i + 1]
    if (next && next.start - p.end > gapTolerance) {
      series.push({ t: p.end, st: null, hz: null })
    } else if (!next) {
      // Terminal point so the line reaches the end of the last bin (matching maxT).
      series.push({ t: p.end, st: 12 * Math.log2(p.hz) - refSemis, hz: p.hz })
    }
  })
  return series
}
